import React, { useCallback, useEffect } from 'react';
import { CardViewContentBlockProps } from './types';
import { Box, Typography } from '@mui/material';
import LibraryCategoryCard from '../../../../common/library-category-card/LibraryCategoryCard';
import { useTranslation } from 'react-i18next';
import RecruitmentIcon from '../../../../../assets/svg/recruitment.svg?react';
import OnboardingIcon from '../../../../../assets/svg/probation.svg?react';
import AgreementIcon from '../../../../../assets/svg/policy.svg?react';
import PolicyIcon from '../../../../../assets/svg/policies_and_procedures.svg?react';
import NewEmployeeManualIcon from '../../../../../assets/svg/new-employee-manual.svg?react';
import EmployeeManagementIcon from '../../../../../assets/svg/employee-management.svg?react';
import TerminationIcon from '../../../../../assets/svg/termination.svg?react';
import ProbationIcon from '../../../../../assets/svg/probation.svg?react';
import ManagingUnderperformanceIcon from '../../../../../assets/svg/growth.svg?react';
import PerformanceManagementIcon from '../../../../../assets/svg/performance.svg?react';
import { TAGS_LABELS } from '../../../../common/constants/tags-labels';
import { Helpers } from '../../../../common/helpers/helpers';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../../../common/hooks/useAppSelector';
import { selectAllCompanies } from '../../../../slices/companies/companiesSlice';

export const EmploymentCategoryContent = ({
  templates,
  setWorkflow,
}: CardViewContentBlockProps) => {
  const { t } = useTranslation();
  const [searchParam] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const workflowItem = searchParam.get('workflow');
  const company = useAppSelector(selectAllCompanies)[0];
  const templatesFilteredByCategory = [...templates].filter(
    (template) =>
      'dl_workflows' in template && 'is_free' in template && template.category === 'Employment'
  );
  const getTemplatesCount = (workflow: string) => {
    return templatesFilteredByCategory.filter((el) => el.dl_workflows.includes(workflow)).length;
  };
  const getFreeTemplatesCount = (workflow: string) => {
    return templatesFilteredByCategory.filter(
      (el) => el.dl_workflows.includes(workflow) && el.is_free
    ).length;
  };
  const getTemplatesCountByWorkflow = (workflow: string) => {
    return templatesFilteredByCategory.filter((el) => el.dl_workflows.includes(workflow)).length;
  };
  const getIcon = (workflow: string) => {
    switch (workflow) {
      case t('tagsLabels.documentWorkflow.employment.recruitment'):
        return <RecruitmentIcon />;
      case t('tagsLabels.documentWorkflow.employment.agreement'):
        return <AgreementIcon />;
      case t('tagsLabels.documentWorkflow.employment.newEmployeeManual'):
        return <NewEmployeeManualIcon />;
      case t('tagsLabels.documentWorkflow.employment.policiesAndProcedures'):
        return <PolicyIcon />;
      case t('tagsLabels.documentWorkflow.employment.induction'):
        return <OnboardingIcon />;
      case t('tagsLabels.documentWorkflow.employment.employeeManagement'):
        return <EmployeeManagementIcon />;
      case t('tagsLabels.documentWorkflow.employment.probation'):
        return <ProbationIcon />;
      case t('tagsLabels.documentWorkflow.employment.performanceManagement'):
        return <PerformanceManagementIcon />;
      case t('tagsLabels.documentWorkflow.employment.managingUnderperformance'):
        return <ManagingUnderperformanceIcon />;
      case t('tagsLabels.documentWorkflow.employment.termination'):
        return <TerminationIcon />;
    }
  };

  const getWorkflowNumber = (workflow: string) => {
    switch (workflow) {
      case t('tagsLabels.documentWorkflow.employment.recruitment'):
        return '1. ';
      case t('tagsLabels.documentWorkflow.employment.agreement'):
        return '2. ';
      case t('tagsLabels.documentWorkflow.employment.newEmployeeManual'):
        return '3. ';
      case t('tagsLabels.documentWorkflow.employment.policiesAndProcedures'):
        return '4. ';
      case t('tagsLabels.documentWorkflow.employment.induction'):
        return '5. ';
      case t('tagsLabels.documentWorkflow.employment.employeeManagement'):
        return '6. ';
      case t('tagsLabels.documentWorkflow.employment.probation'):
        return '7. ';
      case t('tagsLabels.documentWorkflow.employment.performanceManagement'):
        return '8. ';
      case t('tagsLabels.documentWorkflow.employment.managingUnderperformance'):
        return '9. ';
      case t('tagsLabels.documentWorkflow.employment.termination'):
        return '10. ';
    }
  };
  const handleWorkflowItem = useCallback(() => {
    if (workflowItem) {
      setWorkflow(decodeURI(workflowItem));
      navigate(location.pathname);
    }
  }, [workflowItem, setWorkflow, navigate, location]);

  useEffect(() => {
    handleWorkflowItem();
  }, [handleWorkflowItem]);

  return (
    <>
      <Typography
        variant='h3'
        component='h3'
        sx={{ marginBottom: '16px', fontSize: '15px', lineHeight: '24px' }}
      >
        {t('headers.workflows')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '24px',
          backgroundColor: 'transparent',
          flexWrap: 'wrap',
        }}
      >
        {TAGS_LABELS.documentWorkflow.employment.map((el) =>
          getTemplatesCountByWorkflow(t(el)) > 0 ? (
            <LibraryCategoryCard
              key={el}
              title={Helpers.capitalizeTitle(getWorkflowNumber(t(el)) + t(el))}
              description={
                getTemplatesCount(t(el)) +
                (getTemplatesCount(t(el)) > 1 ? ' templates' : ' template')
              }
              hasFreeLabel={
                getFreeTemplatesCount(t(el)) > 0 &&
                !company?.stripe_subscription &&
                !company?.stripe_product_id
              }
              onClick={() => setWorkflow(t(el))}
              icon={getIcon(t(el))}
            />
          ) : null
        )}
      </Box>
    </>
  );
};
